import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import CustomCheckboxFilter from "../../../utils/CustomCheckboxFilter";
import Loading from "../../../utils/Loading";
import SearchFilter from "../../../utils/SearchFilter";
import {
  Button,
  Modal,
  ModalContent,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from "@heroui/react";
import { EyeOff } from "../../../components/admin/DashboardIcons.jsx";
import {
  fetchOperatorBetFilterValues,
  fetchOperatorBetList,
  toAgGridColumnFilters,
} from "../../../utils/operatorBetListServerSide";

ModuleRegistry.registerModules([AllCommunityModule]);

const BetTable = () => {
  const [rowData, setRowData] = useState([]);
  const [selectedBetIds, setSelectedBetIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gridReady, setGridReady] = useState(false);
  const [quickFilterText, setQuickFilterText] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const {
    isOpen: isMessageOpen,
    onOpen: onMessageOpen,
    onOpenChange: onMessageOpenChange,
    onClose: onMessageClose,
  } = useDisclosure();
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("alert");
  const [modalCallback, setModalCallback] = useState(null);
  const [modalAutoCloseMs, setModalAutoCloseMs] = useState(0);
  const [isbetdetails, setIsBetDetails] = useState(false);
  const [selectedBet, setSelectedBet] = useState(null);
  const [betDetailsLoading, setBetDetailsLoading] = useState(false);

  useEffect(() => {
    if (isMessageOpen && modalType === "alert" && modalAutoCloseMs > 0) {
      const timeoutId = setTimeout(() => {
        if (typeof modalCallback === "function") {
          const maybeFn = modalCallback();
          if (typeof maybeFn === "function") {
            maybeFn();
          }
        }
        if (typeof onMessageClose === "function") {
          onMessageClose();
        }
        setModalAutoCloseMs(0);
      }, modalAutoCloseMs);
      return () => clearTimeout(timeoutId);
    }
  }, [
    isMessageOpen,
    modalType,
    modalAutoCloseMs,
    modalCallback,
    onMessageClose,
  ]);
  const handleCancelCheckbox = (event, rowData) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      console.log("Cancel triggered for:", rowData);
      // Perform cancel logic here
      // Optional: confirm with user
      // Optional: call API or update state
    }
  };
  const gridRef = useRef(null);
  const searchRef = useRef("");
  const blockSizeRef = useRef(20);
  const skipSearchRefresh = useRef(true);
  const sortKeyRef = useRef("created_on:desc");
  const filterKeyRef = useRef("{}");
  const listEpochRef = useRef(0);
  const listAbortRef = useRef(null);
  const filterAbortRef = useRef({});

  // useEffect(() => {
  //   setRowData(markets);
  // }, [markets]);
  const invalidateListRequests = () => {
    listEpochRef.current += 1;
    listAbortRef.current?.abort();
    listAbortRef.current = new AbortController();
    return listEpochRef.current;
  };

  const refreshBets = () => {
    invalidateListRequests();
    gridRef.current?.paginationGoToFirstPage?.();
    gridRef.current?.purgeInfiniteCache?.();
  };

  const datasource = useMemo(
    () => ({
      getRows: async (params) => {
        const epoch = listEpochRef.current;
        const signal = listAbortRef.current?.signal;
        try {
          const pageSize = Math.max(1, params.endRow - params.startRow);
          const page = Math.floor(params.startRow / pageSize) + 1;
          const sortModel = params.sortModel?.[0];
          const filterModel =
            gridRef.current?.getFilterModel?.() || params.filterModel || {};

          const data = await fetchOperatorBetList(
            {
              page,
              pageSize,
              search: searchRef.current || "",
              sortField: sortModel?.colId || "created_on",
              sortOrder: sortModel?.sort || "desc",
              columnFilters: toAgGridColumnFilters(filterModel),
            },
            { signal }
          );

          if (epoch !== listEpochRef.current) return;

          const rows = data?.rows || [];
          const hasMore = Boolean(data?.pagination?.hasMore);
          const lastRow = hasMore ? -1 : params.startRow + rows.length;
          params.successCallback(rows, lastRow);
        } catch (error) {
          if (error?.name === "AbortError") return;
          if (epoch !== listEpochRef.current) return;
          console.error("Fetch failed", error);
          params.failCallback();
        } finally {
          if (epoch === listEpochRef.current) {
            setLoading(false);
          }
        }
      },
    }),
    []
  );

  const onGridReady = useCallback(
    (params) => {
      gridRef.current = params.api;
      if (!listAbortRef.current) {
        listAbortRef.current = new AbortController();
      }
      params.api.setGridOption("datasource", datasource);
      setGridReady(true);
    },
    [datasource]
  );

  useEffect(() => {
    searchRef.current = quickFilterText;
    if (!gridReady || !gridRef.current) return undefined;
    if (skipSearchRefresh.current) {
      skipSearchRefresh.current = false;
      return undefined;
    }
    const timer = setTimeout(() => {
      setLoading(true);
      refreshBets();
    }, 450);
    return () => clearTimeout(timer);
  }, [quickFilterText, gridReady]);

  const handleExport = () => {
    if (gridRef.current) {
      gridRef.current.exportDataAsCsv({
        fileName: "Bets_export.csv",
        columnKeys: [
          "bet_id",
          "client_bet_id",
          "round_id",
          "user_id",
          "user_name",
          "match_name",
          "match_map_id",
          "is_closed_type",
          "operator_name",
          "digit",
          "stake",
          "rate",
          "status_id",
          "created_on",
          "ip",
          "match_date",
        ],
        onlySelected: false,
        // processCellCallback: (params) => {
        //   if (params.column.getColId() === "status") {
        //     return params.value === 1 ? "Active" : "Inactive";
        //   }
        //   return params.value;
        // },
      });
    }
  };

  const {
    isOpen: isBetOpen,
    onOpen: onBetOpen,
    onOpenChange: onBetChange,
    onClose: onBetClose,
  } = useDisclosure();

  const getbetdetails = async (bet_id) => {
    try {
      setBetDetailsLoading(true);
      setSelectedBet(null);
      onBetOpen();
      let matchResult = null;
      const response = await fetch(
        process.env.REACT_APP_API_HOST + "/getbetdetils",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("jwt"),
          },
          body: JSON.stringify({
            betId: bet_id,
          }),
        }
      );
      const betdetail = await response.json();
      const matchId = betdetail.data[0].match_id;

      if (matchId) {
        const res = await fetch(
          `${process.env.REACT_APP_API_HOST}/getMatchResultById`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("jwt"),
            },
            body: JSON.stringify({
              match_id: matchId,
            }),
          }
        );
        matchResult = await res.json();
        // console.log("Match Result:", matchResult);
      }
      const betDetails = {
        ...betdetail,
        results: matchResult,
      };
      setSelectedBet(betDetails);
    } catch (err) {
      console.error("Fetch failed", err);
    } finally {
      setBetDetailsLoading(false);
    }
  };

  const calculateJodi = (open, close) => {
    const sumDigits = (numStr) => {
      if (!numStr || numStr.trim() === "") return null; // return null if empty
      const digits = numStr
        .split("")
        .map((d) => parseInt(d, 10))
        .filter((n) => !isNaN(n));
      if (digits.length === 0) return null;
      return digits.reduce((a, b) => a + b, 0);
    };

    const openSum = sumDigits(open);
    const closeSum = sumDigits(close);

    const openDigit = openSum !== null ? (openSum % 10).toString() : "-";
    const closeDigit = closeSum !== null ? (closeSum % 10).toString() : "_";

    // If both missing or invalid
    if (openSum === null && closeSum === null) return "--";

    // Always return 2-character Jodi
    return `${openDigit}${closeDigit}`;
  };

  const autoSizeStrategy = useMemo(() => {
    return {
      type: "fitCellContents",
    };
  }, []);
  const handleCancel = async (id) => {
    setModalMessage("Are you sure you want to cancel this bet?");
    setModalType("confirm");
    setModalCallback(() => () => {
      fetch(`${process.env.REACT_APP_API_HOST}/cancel/bet/${id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
        body: JSON.stringify({ status: 4 }),
      })
        .then(async (response) => {
          if (!response.ok) throw new Error("Failed to cancel the bet");
          return response.json();
        })
        .then(() => {
          setModalMessage("Bet cancelled successfully!");
          setModalType("alert");
          setModalCallback(() => () => {
            refreshBets();
          });
          setModalAutoCloseMs(1000);
          onMessageOpen();
        })
        .catch((error) => {
          console.error("Cancel failed:", error);
          setModalMessage("Failed to cancel the bet.");
          setModalType("alert");
          setModalCallback(null);
          onMessageOpen();
        });
    });
    onMessageOpen();
  };
  const handleBulkCancel = async (rows) => {
    const selectedBetIds = rows.map((row) => row.bet_id);
    if (selectedBetIds.length === 0) {
      setModalMessage("Please select at least one bet to cancel.");
      setModalType("alert");
      setModalCallback(null);
      setModalAutoCloseMs(1000);
      onMessageOpen();
      return;
    }

    setModalMessage("Are you sure you want to cancel the selected bets?");
    setModalType("confirm");
    setModalCallback(() => () => {
      fetch(`${process.env.REACT_APP_API_HOST}/cancel/bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jwt")}`,
        },
        body: JSON.stringify({
          bet_ids: selectedBetIds,
          status: 4,
        }),
      })
        .then(async (response) => {
          if (!response.ok) throw new Error("Failed to cancel selected bets");
          return response.json();
        })
        .then(() => {
          setModalMessage("Selected bets cancelled successfully!");
          setModalType("alert");
          setModalCallback(() => () => {
            refreshBets();
            setSelectedBetIds([]);
          });
          setModalAutoCloseMs(1000);
          onMessageOpen();
        })
        .catch((error) => {
          console.error("Bulk cancel failed:", error);
          setModalMessage("Failed to cancel selected bets.");
          setModalType("alert");
          setModalCallback(null);
          onMessageOpen();
        });
    });
    onMessageOpen();
  };

  const colDefs = useMemo(() => {
    const serverFilter = (column) => ({
      serverSide: true,
      loadOptions: (search) => {
        filterAbortRef.current[column]?.abort();
        const controller = new AbortController();
        filterAbortRef.current[column] = controller;
        return fetchOperatorBetFilterValues(column, search, {
          signal: controller.signal,
          limit: 100,
          offset: 0,
        });
      },
    });

    return [
      {
        headerName: "",
        field: "checkbox",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        sortable: false,
        filter: false,
        width: 100,
        minWidth: 80,
        suppressMenu: true,
        suppressMovable: true,
        cellClass: "ag-center-cols-cell",
        headerClass: "ag-center-header",
        cellStyle: {
          display: "flex",
          justifyContent: "left",
          marginLeft: "-18px",
        },
      },
      {
        field: "bet_id",
        headerName: "Bet Id",
        filter: CustomCheckboxFilter,
        filterParams: serverFilter("bet_id"),
        minWidth: 120,
      },
      {
        field: "client_bet_id",
        headerName: "Client Bet Id",
        filter: CustomCheckboxFilter,
        filterParams: serverFilter("client_bet_id"),
        minWidth: 120,
      },
      {
        field: "round_id",
        headerName: "Round Id",
        filter: CustomCheckboxFilter,
        filterParams: serverFilter("round_id"),
        minWidth: 120,
      },
      {
        field: "user_id",
        headerName: "User Id",
        filter: CustomCheckboxFilter,
        filterParams: serverFilter("user_id"),
        minWidth: 120,
      },
      {
        field: "user_name",
        headerName: "User Name",
        filter: CustomCheckboxFilter,
        filterParams: serverFilter("user_name"),
        minWidth: 150,
      },
      {
        field: "match_date",
        headerName: "Match Date",
        filter: CustomCheckboxFilter,
        filterParams: serverFilter("match_date"),
        minWidth: 150,
      },
      {
        field: "match_name",
        headerName: "Match Name",
        filter: CustomCheckboxFilter,
        filterParams: serverFilter("match_name"),
        minWidth: 150,
      },
      {
        field: "match_map_id",
        headerName: "Match Type",
        filter: CustomCheckboxFilter,
        filterParams: serverFilter("match_map_id"),
        minWidth: 120,
      },
      {
        field: "is_closed_type",
        headerName: "Market Type",
        filter: CustomCheckboxFilter,
        filterParams: serverFilter("is_closed_type"),
        minWidth: 120,
      },
      {
        field: "operator_name",
        headerName: "Operator",
        filter: CustomCheckboxFilter,
        filterParams: serverFilter("operator_name"),
        minWidth: 120,
      },
      {
        field: "digit",
        headerName: "Digit ",
        filter: CustomCheckboxFilter,
        filterParams: serverFilter("digit"),
        minWidth: 100,
      },
      {
        field: "stake",
        headerName: "Stake",
        filter: CustomCheckboxFilter,
        filterParams: serverFilter("stake"),
        minWidth: 100,
      },
      {
        field: "rate",
        headerName: "Rate",
        filter: CustomCheckboxFilter,
        filterParams: serverFilter("rate"),
        minWidth: 100,
      },
      {
        field: "status_id",
        headerName: "Status Id",
        filter: CustomCheckboxFilter,
        filterParams: serverFilter("status_id"),
        minWidth: 120,
      },
      {
        field: "created_on",
        headerName: "Bet Time Date",
        sort: "desc",
        minWidth: 180,
        valueFormatter: (params) => {
          if (!params.value) return "";
          const d = new Date(params.value);
          const pad = (n) => n.toString().padStart(2, "0");
          return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
            d.getDate()
          )} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(
            d.getSeconds()
          )}`;
        },
      },
      {
        field: "ip",
        headerName: "IP Address",
        filter: CustomCheckboxFilter,
        filterParams: serverFilter("ip"),
        minWidth: 150,
      },

      {
        headerName: "Action",
        field: "action",
        sortable: false,
        filter: false,
        minWidth: 120,
        cellRenderer: (params) => {
          if (!params.data) return null;
          const isOpen = params.data.status_id === "Open";

          return (
            <div className="flex gap-2">
              {/* <button
                className={`px-3 py-1 rounded text-sm text-white ${
                  isOpen
                    ? "bg-yellow-500 hover:bg-blue-600"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
                disabled={!isOpen}
                onClick={() => handleCancel(params.data.bet_id)}
              >
                Cancel
              </button> */}
              <button
                className={`px-3 py-1 rounded text-sm text-white bg-blue-600`}
                onClick={() => {
                  getbetdetails(params.data.bet_id);
                }}
              >
                <EyeOff className="w-4 h-4" />
              </button>
            </div>
          );
        },
      },
    ];
  }, []);

  const defaultColDef = {
    sortable: true,
    // Removed flex: 1 to prevent columns from shrinking to fit
  };

  const handleQuickFilterChange = (e) => {
    setQuickFilterText(e.target.value);
  };

  return (
    <div className="shadow rounded-lg">
      {/* Search Input */}
      <div className="flex justify-between">
        <div className=" flex mb-4 gap-2">
          <SearchFilter
            value={quickFilterText}
            onChange={handleQuickFilterChange}
          />

          {/* <button
            className="bg-blue-600 text-white px-6 w-24 py-2.5 rounded hover:bg-blue-700"
            onClick={handleExport}
          >
            Export
          </button> */}
        </div>
        <div className="mb-4">
          {selectedRows.length >= 2 && (
            <div className="mt-4">
              <button
                className="bg-blue-600 hover:bg-yellow text-white px-4 py-2 rounded"
                onClick={() => handleBulkCancel(selectedRows)}
              >
                Cancel Selected ({selectedRows.length})
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        className="ag-theme-alpine bet-table-container bets-paging h-full relative"
        style={{ height: 750, overflowX: "auto" }}
      >
        {loading && (
          <div className="absolute inset-0 z-10 bg-white">
            <Loading />
          </div>
        )}
        <AgGridReact
          columnDefs={colDefs}
          rowSelection="multiple"
          rowModelType="infinite"
          cacheBlockSize={20}
          maxBlocksInCache={1}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={20}
          onGridReady={onGridReady}
          paginationPageSizeSelector={[20, 30, 40, 50]}
          autoSizeStrategy={autoSizeStrategy}
          enableCellTextSelection={true}
          overlayNoRowsTemplate="<span style='padding: 10px; display: block;'>No data found</span>"
          onPaginationChanged={(params) => {
            if (!params.newPageSize) return;
            const nextSize = params.api.paginationGetPageSize();
            if (!nextSize || nextSize === blockSizeRef.current) return;
            blockSizeRef.current = nextSize;
            params.api.setGridOption("cacheBlockSize", nextSize);
            setLoading(true);
            invalidateListRequests();
            params.api.purgeInfiniteCache();
          }}
          onSortChanged={(params) => {
            const sortKey = (params.api.getColumnState?.() || [])
              .filter((col) => col.sort)
              .map((col) => `${col.colId}:${col.sort}`)
              .join(",");
            if (sortKey === sortKeyRef.current) return;
            sortKeyRef.current = sortKey;
            setLoading(true);
            invalidateListRequests();
            params.api.paginationGoToFirstPage();
            params.api.purgeInfiniteCache();
          }}
          onFilterChanged={(params) => {
            const filterKey = JSON.stringify(params.api.getFilterModel?.() || {});
            if (filterKey === filterKeyRef.current) return;
            filterKeyRef.current = filterKey;
            setLoading(true);
            invalidateListRequests();
            params.api.paginationGoToFirstPage();
            params.api.purgeInfiniteCache();
          }}
          onSelectionChanged={(params) => {
            const selected = params.api.getSelectedRows();
            setSelectedRows(selected);
          }}
        />
      </div>
      <Modal
        isOpen={isMessageOpen}
        onOpenChange={onMessageOpenChange}
        classNames={{
          backdrop: "bg-black/35",
          wrapper: "flex items-center justify-center min-h-screen",
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              {modalType === "confirm" && (
                <>
                  <ModalBody className="flex items-start justify-center min-h-[60px]">
                    <p className="text-center font-semibold">{modalMessage}</p>
                  </ModalBody>
                  <ModalFooter className="-mt-4">
                    <Button color="danger" variant="light" onPress={onClose}>
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      onPress={() => {
                        if (typeof modalCallback === "function") {
                          const maybeFn = modalCallback();
                          if (typeof maybeFn === "function") {
                            maybeFn();
                          }
                        }
                        onClose();
                      }}
                    >
                      Yes
                    </Button>
                  </ModalFooter>
                </>
              )}

              {modalType === "alert" && (
                <>
                  <ModalBody className="flex items-center justify-center min-h-[80px]">
                    <p className="text-center font-semibold">{modalMessage}</p>
                  </ModalBody>
                </>
              )}
            </>
          )}
        </ModalContent>
      </Modal>
      <Modal isOpen={isBetOpen} onOpenChange={onBetChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Bet Details</ModalHeader>
              <ModalBody>
                {betDetailsLoading || !selectedBet ? (
                  <Loading />
                ) : (
                  <>
                    {/* Extract common values */}
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-5">
                      <div className="grid grid-cols-3 gap-x-8 gap-y-4 text-sm">
                        {/* Row 1 */}
                        <div>
                          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">
                            Bet ID
                          </p>
                          <p className="text-gray-800 font-medium mt-0.5">
                            {selectedBet.data?.[0]?.bet_id || "--"}
                          </p>
                        </div>

                        <div>
                          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">
                            Round ID
                          </p>
                          <p className="text-gray-800 font-medium mt-0.5">
                            {selectedBet.data?.[0]?.round_id || "--"}
                          </p>
                        </div>

                        <div>
                          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">
                            User ID
                          </p>
                          <p className="text-gray-800 font-medium mt-0.5">
                            {selectedBet.data?.[0]?.user_id || "--"}
                          </p>
                        </div>

                        {/* Row 2 */}
                        <div>
                          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">
                            Market
                          </p>
                          <p className="text-gray-800 font-medium mt-0.5">
                            {selectedBet.results?.data?.market_title || "--"}
                          </p>
                        </div>

                        <div>
                          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">
                            Match
                          </p>
                          <p className="text-gray-800 font-medium mt-0.5">
                            {selectedBet.results?.data?.match_title || "--"}
                          </p>
                        </div>

                        <div>
                          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">
                            Result Date
                          </p>
                          {selectedBet.results?.data?.open_result ||
                          selectedBet.results?.data?.close_result ? (
                            selectedBet.results?.data?.modified_on ? (
                              <p className="text-gray-800  mt-0.5 text-xs">
                                {new Date(
                                  selectedBet.results.data.modified_on
                                ).toLocaleString()}
                              </p>
                            ) : (
                              <p className="italic text-gray-400 mt-0.5">--</p>
                            )
                          ) : (
                            <p className="italic text-gray-500 mt-0.5">
                              Not yet published
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Results */}
                    <div className="grid grid-cols-5 gap-2 text-sm mb-4">
                      {/* Open Result (2 cols wide) */}
                      <div className="col-span-2 p-2 bg-green-50 rounded-md text-green-700 text-center">
                        <span className="font-semibold">Open Result:</span>{" "}
                        {selectedBet.results?.data.open_result &&
                        Number(selectedBet.results?.data.open_result) >= 0
                          ? selectedBet.results.data.open_result
                          : "—"}
                      </div>

                      {/* Jodi Result (1 col wide, smaller) */}
                      <div className="col-span-1 p-2 bg-yellow-50 rounded-md text-yellow-700 text-center font-semibold">
                        Jodi:{" "}
                        {calculateJodi(
                          selectedBet.results?.data.open_result,
                          selectedBet.results?.data.close_result
                        )}
                      </div>
                      {/* Close Result (2 cols wide) */}
                      <div className="col-span-2 p-2 bg-blue-50 rounded-md text-blue-700 text-center">
                        <span className="font-semibold">Close Result:</span>{" "}
                        {selectedBet.results?.data.close_result &&
                        Number(selectedBet.results?.data.close_result) >= 0
                          ? selectedBet.results.data.close_result
                          : "—"}
                      </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm border border-gray-200 rounded-lg">
                        <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                          <tr>
                            <th className="px-3 py-2 text-left">Digit</th>
                            <th className="px-3 py-2 text-left">Bet Type</th>
                            <th className="px-3 py-2 text-right">Stake</th>
                            <th className="px-3 py-2 text-right">Profit</th>
                            <th className="px-3 py-2 text-center">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedBet.data?.map((item) => (
                            <tr
                              key={item.bet_digits_id}
                              className="border-t border-gray-200"
                            >
                              <td className="px-3 py-2">{item.digit}</td>
                              <td className="px-3 py-2">
                                {item.is_closed_type?.data?.[0] == 0
                                  ? "OPEN"
                                  : "CLOSE"}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {item.stake}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {item.status_id === 2
                                  ? item.winning_potential_profit
                                  : "0"}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {item.status_id === 4 ? (
                                  <p className="text-sm italic text-red-300 text-center">
                                    Bet Cancelled
                                  </p>
                                ) : item.status_id === 5 ? (
                                  <p className="text-sm italic text-red-300 text-center">
                                    Bet Abandoned
                                  </p>
                                ) : item.status === "open" ? (
                                  !item.open_result ||
                                  item.open_result === "" ? (
                                    <p className="text-sm italic text-center">
                                      Open
                                    </p>
                                  ) : item.is_win === 1 ? (
                                    <p className="text-sm italic text-green-600 text-center">
                                      Settled Won
                                    </p>
                                  ) : item.is_win === 0 ? (
                                    <p className="text-sm italic text-red-600 text-center">
                                      Settled Loss
                                    </p>
                                  ) : null
                                ) : item.status === "close" ? (
                                  !item.close_result ||
                                  item.close_result === "" ? (
                                    <p className="text-sm italic text-center">
                                      Open
                                    </p>
                                  ) : item.is_win === 1 ? (
                                    <p className="text-sm italic text-green-600 text-center">
                                      Settled Won
                                    </p>
                                  ) : item.is_win === 0 ? (
                                    <p className=" text-sm italic text-red-600 text-center">
                                      Settled Loss
                                    </p>
                                  ) : null
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </ModalBody>
              <ModalFooter>
                <Button
                  color="danger"
                  variant="light"
                  onPress={() => {
                    setSelectedBet(null);
                    setBetDetailsLoading(false);
                    onClose();
                  }}
                >
                  Close
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default BetTable;
